import { isTreePathExpanded, type TreePath } from '@st/util/tree-expansion'
import { paginateChecklistItems } from '@st/formation'
import { titledPageGroups } from '~/st-doc-viewer/page-groups'
import { documentUploadsModule, selUploads, Upload } from '@features/document-uploads'
import { useDocumentUploads } from '~/features/document-uploads/use-document-uploads'
import { DragHandlersProps, DragState, useDraggable } from '@st/react-util/use-draggable'
import {
  DocumentWarningList,
  ScopedFolderMembership,
  StampAnnotation,
  STDocument,
  STDocumentType,
  STOpenFolderState
} from '@st/sdk'
import {
  ArrowDownIcon,
  ArrowRightIcon,
  Button,
  CaretDownIcon,
  ContextMenu,
  ContextMenuItem,
  Dialog,
  DialogButtons,
  DragHandleIcon,
  DocumentTreeView,
  DocumentTreeViewItem,
  DocumentTreeViewSection,
  EyeCrossIcon,
  EyeIcon,
  FileUploadItem,
  MiniDocumentDropZone,
  Modal,
  PencilIcon,
  TrashIcon,
  WarningIcon
} from '@st/theme'
import { Progress } from '@st/util/progress'
import { Section } from '@st/util/section'
import { ReactNode, useState } from 'react'
import { match, P } from '@st/pattern'
import { useProcess, useSelect } from '~/store'
import { DocumentWarning } from '@st/sdk'
import { selDocumentNameById } from './document-warnings'
import { navigateToDocument, navigateToPage } from './st-folder-focus'
import { documentContract } from './document-viewer-extensions'
import { DeleteContractDialog } from './document-viewer-extensions/delete-contract-dialog'
import {
  canToggleDocumentVisibility,
  getDocumentSections,
  hasUploadedDocuments,
  selSidebarDocumentWarnings,
  selSidebarDocuments,
  selFolderState,
  selDocumentSignOffAnnotations,
  selDocumentTooltip,
  selSelectedDoc,
  selExpandedPaths
} from './st-folder-selectors'
import type { STFolderMessage } from './st-folder-module'
import { STMoveDocumentDialog } from './st-move-document-dialog'
import { STRenameDocumentDialog } from './st-rename-document-dialog'
import { ReportDocumentIssueDialog } from './report-document-issue-dialog'

type DocumentAction =
  | { type: 'markUnread'; doc: STDocument }
  | { type: 'toggleVisibility'; doc: STDocument }
  | { type: 'rename'; doc: STDocument }
  | { type: 'delete'; doc: STDocument }
  | Extract<STFolderMessage, { type: 'downloadDocument' }>
  | { type: 'move'; doc: STDocument }
  | { type: 'reportIssue'; doc: STDocument }

/**
 * The left sidebar that lets you switch between documents.
 *
 * Clicking on an item swaps the currently selected documetn in the center area.
 */
export function STDocumentSidebar() {
  const stFolder = useProcess('stFolder')
  const documentUploads = useDocumentUploads()

  const uploads = useSelect('documentUploads', selUploads)
  const folderState = useSelect('stFolder', selFolderState)
  const documents = useSelect('stFolder', selSidebarDocuments)
  const selectedDoc = useSelect('stFolder', selSelectedDoc)
  const expandedPaths = useSelect('stFolder', selExpandedPaths)
  const documentWarnings = useSelect('stFolder', selSidebarDocumentWarnings)
  const documentNameById = selDocumentNameById(documents)

  const [documentAction, setDocumentAction] = useState<DocumentAction | undefined>()

  if (!folderState) {
    return null
  }

  const folderId = folderState.folderId

  function onDropFiles(dt: DataTransfer) {
    for (const file of dt.files) {
      documentUploads.upload({ file, folderId })
    }
  }

  return (
    <>
      <FolderDocumentSidebar
        state={folderState}
        documents={documents}
        uploads={uploads}
        selectedDocId={selectedDoc?.id}
        expandedPaths={expandedPaths}
        onToggleExpanded={(path) =>
          stFolder.send({ type: 'treeExpansionToggled', path })
        }
        onDropFiles={onDropFiles}
        onSelect={(doc) =>
          stFolder.send({ type: 'navigationRequested', request: navigateToDocument(doc.id) })
        }
        onNavigationRequested={(doc, pageId) =>
          stFolder.send({
            type: 'navigationRequested',
            request: navigateToPage(doc.id, pageId)
          })
        }
        onClearUpload={(uploadId) => documentUploads.send({ type: 'clearUpload', uploadId })}
        documentWarnings={documentWarnings}
        documentNameById={documentNameById}
        onReorderDocument={(doc, referenceDoc, relation) =>
          stFolder.send({
            type: 'requestEnqueued',
            request: {
              type: 'folders/reorderDocument',
              folderId: folderState.folderId,
              documentId: doc.id,
              referenceDocumentId: referenceDoc.id,
              relation
            }
          })
        }
        onDocumentAction={(action) => {
          switch (action.type) {
            case 'delete':
            case 'move':
            case 'rename':
            case 'reportIssue':
              setDocumentAction(action)
              break
            case 'downloadDocument':
              stFolder.send(action)
              break
            case 'markUnread':
              stFolder.send({
                type: 'requestEnqueued',
                request: {
                  type: 'folders/markDocumentAsUnread',
                  folderId: folderState.folderId,
                  documentId: action.doc.id
                }
              })
              break
            case 'toggleVisibility':
              stFolder.send({
                type: 'requestEnqueued',
                request: {
                  type: 'folders/setDocumentVisibility',
                  folderId: folderState.folderId,
                  documentId: action.doc.id,
                  visibility: action.doc.visibility == 'organization' ? 'all' : 'organization'
                }
              })
              break
          }
        }}
      />
      {match(documentAction)
        .with({ type: 'move' }, ({ doc }) => {
          return (
            <Modal isOpen={true}>
              <STMoveDocumentDialog
                folderId={folderState.folderId}
                doc={doc}
                onClose={() => setDocumentAction(undefined)}
              />
            </Modal>
          )
        })
        .with({ type: 'rename' }, ({ doc }) => {
          return (
            <Modal isOpen={true}>
              <STRenameDocumentDialog
                folderId={folderState.folderId}
                doc={doc}
                onClose={() => setDocumentAction(undefined)}
              />
            </Modal>
          )
        })
        .with({ type: 'delete' }, ({ doc }) => {
          const contract = documentContract(folderState, doc)

          return (
            <Modal isOpen={true}>
              {match(contract)
                .with(P.nonNullable, ({ id: contractId, kind }) => (
                  <DeleteContractDialog
                    kind={match(kind)
                      .with('engagement_letter', () => 'engagement_letter' as const)
                      .with('tax_return_delivery', () => 'tax_return_delivery' as const)
                      .otherwise(() => {
                        throw new Error(`Unknown contract kind: ${kind}`)
                      })}
                    onClose={() => setDocumentAction(undefined)}
                    onConfirm={() => {
                      stFolder.send({
                        type: 'requestEnqueued',
                        request: {
                          type: 'folders/deleteContract',
                          folderId: folderState.folderId,
                          contractId: contractId!
                        }
                      })
                      setDocumentAction(undefined)
                    }}
                  />
                ))
                .with(P.nullish, () => (
                  <DeleteDocumentDialog
                    folderId={folderState.folderId}
                    doc={doc}
                    onClose={() => setDocumentAction(undefined)}
                  />
                ))
                .exhaustive()}
            </Modal>
          )
        })
        .with({ type: 'reportIssue' }, ({ doc }) => {
          return (
            <Modal isOpen={true}>
              <ReportDocumentIssueDialog
                folderId={folderState.folderId}
                doc={doc}
                onClose={() => setDocumentAction(undefined)}
              />
            </Modal>
          )
        })
        .otherwise(() => null)}
    </>
  )
}

type FolderDocumentSidebarProps = {
  state: STOpenFolderState
  documents: STDocument[]
  uploads: Upload[]
  selectedDocId: string | undefined
  expandedPaths: TreePath[]
  onToggleExpanded: (path: TreePath) => void
  onDropFiles: (dt: DataTransfer) => void
  onSelect?: (doc: STDocument) => void
  onNavigationRequested?: (doc: STDocument, pageId: string) => void
  onClearUpload?: (uploadId: string) => void
  onDocumentAction?: (action: DocumentAction) => void
  onReorderDocument?: (
    doc: STDocument,
    referenceDoc: STDocument,
    relation: 'before' | 'after'
  ) => void
  documentWarnings: Record<string, DocumentWarningList>
  documentNameById: Record<string, string>
}
function FolderDocumentSidebar({
  state,
  documents,
  uploads,
  selectedDocId,
  expandedPaths,
  onToggleExpanded,
  onDropFiles,
  onSelect,
  onNavigationRequested,
  onClearUpload,
  onDocumentAction,
  onReorderDocument,
  documentWarnings,
  documentNameById
}: FolderDocumentSidebarProps) {
  // Archived binders are UI read-only for document mutation actions.
  const isArchived = state.folder.archived
  const sections = getDocumentSections({
    documentTypes: state.documentTypes,
    documents
  })
  const { dragState, handlers } = useDraggable<STDocument>({
    onDrop: ({ item, target, relation }) => onReorderDocument?.(item, target, relation),
    getKey: (doc) => doc.id
  })
  const noDocumentsUploaded = !hasUploadedDocuments(documents)

  return (
    <DocumentTreeView
      bottom={
        <div className="flex flex-col gap-2">
          {uploads.map((upload) => {
            return (
              <FileUploadItem
                key={upload.id}
                value={Progress.toNumber(upload.progress)}
                status={upload.status}
                onClose={upload.status == 'failed' ? () => onClearUpload?.(upload.id) : undefined}
              >
                {upload.filename}
              </FileUploadItem>
            )
          })}

          {/* Archived binders should not expose upload affordances. */}
          {isArchived ? null : (
            <MiniDocumentDropZone caption="Documents up to 100MB" onDrop={onDropFiles} />
          )}
        </div>
      }
    >
      {sections.map((section) => {
        const isSectionReorderable = canReorderSection(section, isArchived)
        const isProcessingSection = section.heading.id == 'processing'

        return (
          <DocumentTreeViewSection
            key={section.heading.id}
            title={section.heading.name}
            ariaLabel={isProcessingSection ? 'Processing documents' : section.heading.name}
            ariaBusy={isProcessingSection || undefined}
          >
            {section.items.map((doc) => {
              const isDocumentReorderable = isSectionReorderable && doc.permissions.includes('move')
              const pageGroups = match(doc.id)
                .with('checklist', () =>
                  paginateChecklistItems(state.checklistItems)
                    .filter((page) => page.isFirst)
                    .map((page) => ({
                      title: match(page.phase)
                        .with('intake', () => 'Intake checklist')
                        .with('delivery', () => 'Tax payment checklist')
                        .exhaustive(),
                      pageRange: { first: page.number, last: page.number },
                      metadata: {}
                    }))
                )
                .otherwise(() => titledPageGroups(doc.pageGroups))

              return (
                <STDocumentTreeViewItem
                  key={doc.id}
                  doc={doc}
                  onClick={() => onSelect?.(doc)}
                  onDocumentAction={onDocumentAction}
                  isSelected={selectedDocId == doc.id}
                  isExpanded={isTreePathExpanded(expandedPaths, [doc.id])}
                  onToggleExpanded={() => onToggleExpanded([doc.id])}
                  memberships={state.memberships}
                  warnings={documentWarnings[doc.id]?.warnings ?? []}
                  signOffAnnotations={selDocumentSignOffAnnotations(doc)}
                  documentNameById={documentNameById}
                  isUnread={doc.readStatus == 'unread'}
                  isArchived={isArchived}
                  onNavigationRequested={onNavigationRequested}
                  pageGroups={pageGroups}
                  draggable={isDocumentReorderable}
                  dragState={dragState}
                  {...(isDocumentReorderable ? handlers(doc) : {})}
                />
              )
            })}
          </DocumentTreeViewSection>
        )
      })}
      {noDocumentsUploaded && (
        <DocumentTreeViewSection title="Uploaded documents">
          <DocumentTreeViewItem>
            <div role="status" aria-live="polite" className="text-sm text-gray-500">
              No documents uploaded
            </div>
          </DocumentTreeViewItem>
        </DocumentTreeViewSection>
      )}
    </DocumentTreeView>
  )
}

function canReorderSection(section: Section<STDocumentType, STDocument>, isArchived: boolean) {
  if (isArchived || section.items.length < 2) {
    return false
  }

  return !['stanfordtax', 'processing', 'invalid_category'].includes(section.heading.id)
}

function STDocumentTreeViewItem({
  doc,
  onDocumentAction,
  onClick,
  onNavigationRequested,
  pageGroups,
  isSelected,
  isExpanded,
  onToggleExpanded,
  memberships,
  signOffAnnotations,
  warnings,
  isUnread,
  isArchived,
  documentNameById,
  dragState,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onDragLeave
}: {
  doc: STDocument
  memberships: ScopedFolderMembership[]
  signOffAnnotations: StampAnnotation[]
  warnings: DocumentWarning[]
  isUnread: boolean
  isArchived: boolean
  documentNameById?: Record<string, string>
  onDocumentAction?: (action: DocumentAction) => void
  onClick?: (doc: STDocument) => void
  onNavigationRequested?: (doc: STDocument, pageId: string) => void
  pageGroups: ReturnType<typeof titledPageGroups>
  isSelected?: boolean
  isExpanded: boolean
  onToggleExpanded: () => void
  dragState: DragState<STDocument>
} & Partial<DragHandlersProps>) {
  const hasPageGroups = pageGroups.length > 0
  const dragStatus = (() => {
    if (dragState.status == 'hovering' && dragState.target == doc) {
      return dragState.relation
    }

    if ((dragState.status == 'hovering' || dragState.status == 'moving') && dragState.item == doc) {
      return 'dragging'
    }

    return undefined
  })()
  const documentMenu = (
    <ContextMenu aria-label={`Document actions for ${doc.name}`}>
      {doc.permissions.includes('download') && (
        <ContextMenuItem
          icon={<ArrowDownIcon className="size-4" />}
          onClick={() => onDocumentAction?.({ type: 'downloadDocument', documentId: doc.id })}
        >
          Download
        </ContextMenuItem>
      )}

      {doc.permissions.includes('move') && (
        <ContextMenuItem
          icon={<ArrowRightIcon />}
          onClick={() => onDocumentAction?.({ type: 'move', doc })}
        >
          Move to...
        </ContextMenuItem>
      )}

      {doc.permissions.includes('rename') && (
        <ContextMenuItem
          icon={<PencilIcon className="size-3.5" />}
          onClick={() => onDocumentAction?.({ type: 'rename', doc })}
        >
          Rename
        </ContextMenuItem>
      )}

      {doc.readStatus == 'read' && doc.documentTypeId != 'stanfordtax' && (
        <ContextMenuItem
          icon={<EyeIcon className="size-3.5 text-gray-500" />}
          onClick={() => onDocumentAction?.({ type: 'markUnread', doc })}
        >
          Mark unread
        </ContextMenuItem>
      )}

      {canToggleDocumentVisibility(doc) && (
        <ContextMenuItem
          icon={
            doc.visibility == 'organization' ? (
              <EyeCrossIcon className="size-3.5 text-gray-500" />
            ) : (
              <EyeIcon className="size-3.5 text-gray-500" />
            )
          }
          onClick={() => onDocumentAction?.({ type: 'toggleVisibility', doc })}
        >
          {doc.visibility == 'organization' ? 'Unhide from client' : 'Hide from client'}
        </ContextMenuItem>
      )}

      {doc.permissions.includes('report_issue') && (
        <ContextMenuItem
          icon={<WarningIcon className="size-3.5" />}
          onClick={() => onDocumentAction?.({ type: 'reportIssue', doc })}
        >
          Report issue
        </ContextMenuItem>
      )}

      {doc.permissions.includes('delete') && (
        <ContextMenuItem
          icon={<TrashIcon className="size-3.5" />}
          onClick={() => onDocumentAction?.({ type: 'delete', doc })}
        >
          Delete
        </ContextMenuItem>
      )}
    </ContextMenu>
  )

  const disclosure = hasPageGroups ? (
    <button
      type="button"
      className="flex size-4 items-center justify-center text-gray-700"
      aria-label={`${isExpanded ? 'Collapse' : 'Expand'} page groups for ${doc.name}`}
      aria-expanded={isExpanded}
      onClick={(event) => {
        event.stopPropagation()
        onToggleExpanded()
      }}
    >
      <CaretDownIcon
        className={`h-3 w-3 cursor-pointer ${isExpanded ? 'rotate-0' : '-rotate-90'}`}
      />
    </button>
  ) : (
    <div className="size-4" aria-hidden />
  )

  const leading = hasPageGroups ? (
    disclosure
  ) : (
    <div className="flex size-4 items-center justify-center">
      {draggable ? (
        <div aria-label={`Reorder document ${doc.name}`} role="img">
          <DragHandleIcon
            className={[
              'invisible cursor-move text-[#545666] group-hover/document-row:visible',
              dragStatus == 'dragging' ? 'visible' : ''
            ]
              .filter(Boolean)
              .join(' ')}
          />
        </div>
      ) : (
        <div className="size-4" aria-hidden />
      )}
    </div>
  )

  return (
    <div className="flex flex-col">
      <div
        className={[
          'group/document-row',
          dragStatus == 'before' ? 'shadow-[inset_0_2px_0_0_#354dcc]' : '',
          dragStatus == 'after' ? 'shadow-[inset_0_-2px_0_0_#354dcc]' : '',
          dragStatus == 'dragging' ? 'opacity-80' : ''
        ]
          .filter(Boolean)
          .join(' ')}
        draggable={draggable}
        onDragStart={onDragStart}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onDragLeave={onDragLeave}
      >
        <DocumentTreeViewItem
          leading={leading}
          tip={selDocumentTooltip(doc, {
            memberships,
            signOffAnnotations,
            warnings,
            documentNameById
          })}
          badges={
            <DocumentStatusBadges
              signOffAnnotations={signOffAnnotations}
              warnings={warnings}
              visibility={doc.visibility}
            />
          }
          // Keep row alignment stable in archived binders by reserving menu space.
          menu={isArchived ? <div className="invisible size-4" aria-hidden /> : documentMenu}
          isSelected={isSelected}
          isUnread={isUnread}
          onClick={() => onClick?.(doc)}
        >
          <span className="overflow-hidden text-ellipsis text-nowrap">{doc.name}</span>
        </DocumentTreeViewItem>
      </div>

      {hasPageGroups && isExpanded
        ? pageGroups.map((group, index) => {
            const title = group.title!
            const pageId = String(group.metadata.pageId ?? group.pageRange.first)
            return (
              <DocumentPageGroupRow
                key={`${doc.id}:${pageId}:${index}`}
                ariaLabel={`Navigate to ${title} page group in ${doc.name}`}
                title={title}
                isHighlighted={group.metadata.highlight == true}
                onClick={() => onNavigationRequested?.(doc, pageId)}
                menu={match(
                  !isArchived && doc.permissions.includes('download') && doc.urls.view != undefined
                )
                  .with(true, () => (
                    <ContextMenu aria-label={`Page group actions for ${title}`}>
                      <ContextMenuItem
                        icon={<ArrowDownIcon className="size-4" />}
                        onClick={() =>
                          onDocumentAction?.({
                            type: 'downloadDocument',
                            documentId: doc.id,
                            pages: [group.pageRange],
                            filename: `${doc.name.replace(/\.pdf$/i, '')} - ${title}.pdf`
                          })
                        }
                      >
                        Download
                      </ContextMenuItem>
                    </ContextMenu>
                  ))
                  .otherwise(() => undefined)}
              />
            )
          })
        : null}
    </div>
  )
}

function DocumentPageGroupRow({
  ariaLabel,
  title,
  isHighlighted,
  onClick,
  menu
}: {
  ariaLabel: string
  title: string
  isHighlighted: boolean
  onClick: () => void
  menu?: ReactNode
}) {
  return (
    <div className="hover:bg-white-200 group flex h-8 items-center border-l-4 border-transparent pr-4 text-sm text-gray-700">
      <button
        type="button"
        aria-label={ariaLabel}
        className="flex h-full min-w-0 grow items-center pl-8 text-left"
        onClick={onClick}
      >
        {/* Child rows are one indent step deeper than document rows. */}
        <div className="w-5" aria-hidden />
        <div className="ml-1 flex min-w-0 items-center gap-1">
          {isHighlighted ? <OrganizerPageGroupBadge /> : null}
          <span className="min-w-0 overflow-hidden text-ellipsis text-nowrap font-normal">
            {title}
          </span>
        </div>
      </button>
      {menu && <div className="flex shrink-0 items-center">{menu}</div>}
    </div>
  )
}

function OrganizerPageGroupBadge() {
  return <div className="-ml-2 size-1.5 shrink-0 rounded-full bg-yellow-600" />
}

// A flexible container that grows horizontally to fit multiple sign-off dots while
// keeping a consistent row height with other badge/icons in the list.
function SignOffBadges({ signOffAnnotations }: { signOffAnnotations: StampAnnotation[] }) {
  return (
    <div className="flex h-3.5 items-center justify-center gap-0.5">
      {signOffAnnotations.map((annot) => {
        return (
          <div
            className="size-1.5 rounded-full"
            key={annot.id}
            style={{ backgroundColor: annot.backgroundColor }}
          />
        )
      })}
    </div>
  )
}

function DocumentStatusBadges({
  signOffAnnotations,
  warnings,
  visibility
}: {
  signOffAnnotations: StampAnnotation[]
  warnings: DocumentWarning[]
  visibility: STDocument['visibility']
}) {
  const hasSignOffBadges = signOffAnnotations.length > 0
  const hasWarningBadge = warnings.length > 0
  const hasVisibilityBadge = visibility == 'organization'

  if (!hasSignOffBadges && !hasWarningBadge && !hasVisibilityBadge) {
    return null
  }

  return (
    <div className="flex items-center gap-1.5">
      {hasWarningBadge ? <WarningIcon className="size-3.5 text-orange-500" /> : null}
      {hasSignOffBadges ? <SignOffBadges signOffAnnotations={signOffAnnotations} /> : null}
      {hasVisibilityBadge ? <EyeCrossIcon className="size-3.5 text-gray-500" /> : null}
    </div>
  )
}

function DeleteDocumentDialog({
  folderId,
  doc,
  onClose
}: {
  folderId: string
  doc: STDocument
  onClose: () => void
}) {
  const stFolder = useProcess('stFolder')

  return (
    <Dialog
      title="Confirm delete"
      subtitle={`Please confirm whether you would like to delete ${doc.name}`}
      buttons={
        <DialogButtons>
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={() => {
              stFolder.send({
                type: 'requestEnqueued',
                request: {
                  type: 'folders/deleteDocument',
                  folderId,
                  documentId: doc.id
                }
              })
              onClose()
            }}
          >
            Delete
          </Button>
        </DialogButtons>
      }
    />
  )
}
